/**
 * useTileDrag - Touch-friendly drag & drop for home screen tiles.
 *
 * Provides long-press to enter edit mode (iOS-style wiggle), then:
 *   - Drag tiles to reorder within the main grid
 *   - Drag tiles into/out of the dock shortcut area
 *
 * Uses pointer events for unified mouse + touch handling.
 * No external library dependencies.
 *
 * Design decisions:
 * - Long press (500ms) activates edit mode (prevents accidental drags)
 * - A floating clone follows the finger/cursor during drag
 * - Drop zones: main grid cells + dock area
 * - Layout changes are committed to the homescreen store on drop
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useHomeScreenStore } from '@room/shared-ui';

const LONG_PRESS_MS = 500;
const DRAG_THRESHOLD_PX = 8;

export interface DragState {
  /** Whether edit/wiggle mode is active */
  isEditMode: boolean;
  /** ID of the tile currently being dragged (null if none) */
  draggingTileId: string | null;
  /** Current pointer position for the floating drag clone */
  dragPosition: { x: number; y: number } | null;
  /** Section ID the drag clone is currently hovering over */
  hoverSection: string | null;
  /** Index within the hover section where tile will be inserted */
  hoverIndex: number | null;
}

export interface TileDragHandlers {
  /** Attach to each tile's pointerdown */
  onTilePointerDown: (tileId: string, sectionId: string, e: React.PointerEvent) => void;
  /** Exit edit/wiggle mode */
  exitEditMode: () => void;
  /** Remove a tile from dock (move to main grid) */
  removeTileFromDock: (tileId: string) => void;
  /** Current drag state for rendering */
  dragState: DragState;
}

export function useTileDrag(): TileDragHandlers {
  const moveTile = useHomeScreenStore((s) => s.moveTile);
  const reorderTiles = useHomeScreenStore((s) => s.reorderTiles);
  const getSections = useHomeScreenStore((s) => s.getSections);

  const [dragState, setDragState] = useState<DragState>({
    isEditMode: false,
    draggingTileId: null,
    dragPosition: null,
    hoverSection: null,
    hoverIndex: null,
  });

  // Use refs for hover state so commitDrop always reads the latest values
  // (avoids stale closure when onPointerUp fires)
  const hoverSectionRef = useRef<string | null>(null);
  const hoverIndexRef = useRef<number | null>(null);

  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const currentTile = useRef<{ id: string; section: string } | null>(null);
  const isDragging = useRef(false);
  const longPressActive = useRef(false);

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    longPressActive.current = false;
  }, []);

  // Handle long press activation → enter edit mode
  const onTilePointerDown = useCallback(
    (tileId: string, sectionId: string, e: React.PointerEvent) => {
      // Only handle primary button / single touch
      if (e.button !== 0) return;

      startPos.current = { x: e.clientX, y: e.clientY };
      currentTile.current = { id: tileId, section: sectionId };

      clearLongPress();
      longPressActive.current = true;

      longPressTimer.current = setTimeout(() => {
        longPressActive.current = false;
        // Activate edit mode on long press
        setDragState((prev) => ({
          ...prev,
          isEditMode: true,
          draggingTileId: tileId,
          dragPosition: startPos.current,
          hoverSection: sectionId,
          hoverIndex: null,
        }));
        isDragging.current = true;

        // Haptic feedback if available
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      }, LONG_PRESS_MS);
    },
    [clearLongPress]
  );

  // Pointer move: update drag position & detect hover targets
  useEffect(() => {
    if (!dragState.isEditMode) return;

    const onPointerMove = (e: PointerEvent) => {
      if (!isDragging.current || !dragState.draggingTileId) return;

      // Cancel long press if moved beyond threshold before activation
      if (startPos.current && longPressTimer.current) {
        const dx = e.clientX - startPos.current.x;
        const dy = e.clientY - startPos.current.y;
        if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
          clearLongPress();
        }
      }

      // Update floating position
      setDragState((prev) => ({
        ...prev,
        dragPosition: { x: e.clientX, y: e.clientY },
      }));

      // Detect which section + index we're hovering over
      detectDropTarget(e.clientX, e.clientY);
    };

    const onPointerUp = () => {
      clearLongPress();

      if (isDragging.current && dragState.draggingTileId) {
        commitDrop();
      }

      isDragging.current = false;
      setDragState((prev) => ({
        ...prev,
        draggingTileId: null,
        dragPosition: null,
        hoverSection: null,
        hoverIndex: null,
      }));
    };

    const onPointerCancel = () => {
      clearLongPress();
      isDragging.current = false;
      setDragState((prev) => ({
        ...prev,
        draggingTileId: null,
        dragPosition: null,
        hoverSection: null,
        hoverIndex: null,
      }));
    };

    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', onPointerCancel);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', onPointerCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragState.isEditMode, dragState.draggingTileId]);

  // Cancel long press on scroll; suppress native context menu on tiles
  useEffect(() => {
    const cancel = () => clearLongPress();
    const onContextMenu = (e: MouseEvent) => {
      // Suppress native context menu (iOS link preview) while long-press
      // is pending or edit mode is active
      if (longPressActive.current || dragState.isEditMode) {
        e.preventDefault();
      }
      cancel();
    };
    window.addEventListener('scroll', cancel, true);
    window.addEventListener('contextmenu', onContextMenu);
    return () => {
      window.removeEventListener('scroll', cancel, true);
      window.removeEventListener('contextmenu', onContextMenu);
    };
  }, [clearLongPress, dragState.isEditMode]);

  // Cancel long press if user moves finger before edit mode activates.
  // This prevents accidental activation when swiping between pages.
  useEffect(() => {
    const handleMove = (e: PointerEvent) => {
      if (!longPressActive.current || !startPos.current) return;
      const dx = e.clientX - startPos.current.x;
      const dy = e.clientY - startPos.current.y;
      if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD_PX) {
        clearLongPress();
      }
    };

    const handleUp = () => {
      if (longPressActive.current) {
        clearLongPress();
      }
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
    };
  }, [clearLongPress]);

  const setHover = useCallback((section: string | null, index: number | null) => {
    hoverSectionRef.current = section;
    hoverIndexRef.current = index;
    setDragState((prev) => ({
      ...prev,
      hoverSection: section,
      hoverIndex: index,
    }));
  }, []);

  const detectDropTarget = useCallback((x: number, y: number) => {
    // Check dock area first
    const dockEl = document.querySelector('[data-droppable="dock"]');
    if (dockEl) {
      const rect = dockEl.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        // Find insertion index within dock (skip the tile being dragged)
        const dockTiles = Array.from(dockEl.querySelectorAll('[data-tile-id]'));
        const draggingId = currentTile.current?.id;
        const visibleTiles = dockTiles.filter(
          (el) => el.getAttribute('data-tile-id') !== draggingId
        );
        let insertIndex = visibleTiles.length;
        for (let i = 0; i < visibleTiles.length; i++) {
          const tileRect = visibleTiles[i].getBoundingClientRect();
          const midX = tileRect.left + tileRect.width / 2;
          if (x < midX) {
            insertIndex = i;
            break;
          }
        }
        setHover('dock', insertIndex);
        return;
      }
    }

    // Check main grid area
    const gridEl = document.querySelector('[data-droppable="main"]');
    if (gridEl) {
      const rect = gridEl.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        // Find nearest grid cell (skip the tile being dragged)
        const gridTiles = Array.from(gridEl.querySelectorAll('[data-tile-id]'));
        const draggingId = currentTile.current?.id;
        const visibleTiles = gridTiles.filter(
          (el) => el.getAttribute('data-tile-id') !== draggingId
        );
        let insertIndex = visibleTiles.length;
        let minDist = Infinity;

        for (let i = 0; i < visibleTiles.length; i++) {
          const tileRect = visibleTiles[i].getBoundingClientRect();
          const centerX = tileRect.left + tileRect.width / 2;
          const centerY = tileRect.top + tileRect.height / 2;
          const dist = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
          if (dist < minDist) {
            minDist = dist;
            insertIndex = i;
          }
        }

        setHover('main', insertIndex);
        return;
      }
    }

    // Not hovering over any valid section
    setHover(currentTile.current?.section || 'main', null);
  }, [setHover]);

  const commitDrop = useCallback(() => {
    const tileId = currentTile.current?.id;
    // Read from refs for the latest hover state (avoids stale closure)
    const targetSection = hoverSectionRef.current;
    const targetIndex = hoverIndexRef.current;

    if (!tileId || !targetSection) return;

    const sections = getSections();
    const sourceSection = currentTile.current?.section || 'main';
    const targetSec = sections.find((s) => s.id === targetSection);

    if (!targetSec) return;

    if (sourceSection === targetSection) {
      // Reorder within the same section
      const currentIds = targetSec.items.map((t) => t.id);
      const fromIndex = currentIds.indexOf(tileId);
      if (fromIndex === -1) return;

      const newIds = [...currentIds];
      newIds.splice(fromIndex, 1);
      const insertAt =
        targetIndex !== null
          ? Math.min(targetIndex, newIds.length)
          : newIds.length;
      newIds.splice(insertAt, 0, tileId);

      reorderTiles(targetSection, newIds);
    } else {
      // Move between sections
      const insertOrder =
        targetIndex !== null
          ? targetIndex
          : targetSec.items.length;
      moveTile(tileId, targetSection, insertOrder);

      // Reorder the target section to ensure sequential order values
      const targetItems = targetSec.items.filter((t) => t.id !== tileId);
      const newIds = targetItems.map((t) => t.id);
      newIds.splice(Math.min(insertOrder, newIds.length), 0, tileId);
      reorderTiles(targetSection, newIds);

      // Also reorder source section to close the gap
      const sourceSec = sections.find((s) => s.id === sourceSection);
      if (sourceSec) {
        const sourceIds = sourceSec.items
          .filter((t) => t.id !== tileId)
          .map((t) => t.id);
        reorderTiles(sourceSection, sourceIds);
      }

      // Update currentTile ref so subsequent operations know the new section
      currentTile.current = { id: tileId, section: targetSection };
    }
  }, [getSections, moveTile, reorderTiles]);

  const exitEditMode = useCallback(() => {
    clearLongPress();
    isDragging.current = false;
    hoverSectionRef.current = null;
    hoverIndexRef.current = null;
    setDragState({
      isEditMode: false,
      draggingTileId: null,
      dragPosition: null,
      hoverSection: null,
      hoverIndex: null,
    });
  }, [clearLongPress]);

  /** Remove a tile from the dock by moving it to the end of the main grid */
  const removeTileFromDock = useCallback((tileId: string) => {
    const sections = getSections();
    const mainSec = sections.find((s) => s.id === 'main');
    const dockSec = sections.find((s) => s.id === 'dock');
    if (!mainSec || !dockSec) return;

    const newOrder = mainSec.items.length;
    moveTile(tileId, 'main', newOrder);

    // Reorder main to include the new tile at the end
    const mainIds = [...mainSec.items.map((t) => t.id), tileId];
    reorderTiles('main', mainIds);

    // Close gap in dock
    const dockIds = dockSec.items.filter((t) => t.id !== tileId).map((t) => t.id);
    reorderTiles('dock', dockIds);
  }, [getSections, moveTile, reorderTiles]);

  return {
    onTilePointerDown,
    exitEditMode,
    removeTileFromDock,
    dragState,
  };
}
